/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Chip, Typography } from '@mui/material';
import React from 'react';
import { usePluginsTranslation } from '../../../utils/i18n';
import type { PluginCatalogEntry } from '../pluginDebugTypes';

interface PluginInfoProps {
  plugin: PluginCatalogEntry;
}

export const PluginInfo: React.FC<PluginInfoProps> = ({ plugin }) => {
  const { t } = usePluginsTranslation();

  return (
    <Box data-testid="plugin-debug-info" sx={{ px: 2, py: 1 }}>
      <Typography gutterBottom variant="subtitle2">
        {t('plugin-information')}
      </Typography>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="body2">
          <strong>{plugin.name}</strong>
        </Typography>
        {!!plugin.version && (
          <Chip
            label={t('plugin-version', { version: plugin.version })}
            size="small"
          />
        )}
        {!!plugin.type && (
          <Chip color="primary" label={plugin.type} size="small" />
        )}
        {plugin.scopes.map((scope) => (
          <Chip
            color="info"
            key={scope}
            label={scope}
            size="small"
            variant="outlined"
          />
        ))}
        <Chip
          label={t('plugin-instance-count', {
            count: plugin.instanceIds.length,
          })}
          size="small"
          variant="outlined"
        />
      </Box>
      {!!plugin.description && (
        <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
          {plugin.description}
        </Typography>
      )}
    </Box>
  );
};
